import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Checkbox,
  InputNumber,
  message,
  Space,
  Tag,
  Spin,
  Empty,
  Row,
  Col,
  Typography,
  Radio,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import dayjs from "dayjs";
import CustomPagination from "../paginations/CustomPagination";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const OfferManagement = () => {
  const [form] = Form.useForm();
  const [offers, setOffers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectType, setSelectType] = useState("description"); // New state for radio button
  const token = localStorage.getItem("token");

  // Fetch all offers
  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/offers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOffers(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data?.length || 0,
      }));
    } catch (err) {
      message.error("Failed to fetch offers");
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies and update state
  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/company-master`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data || []);
      return response.data || [];
    } catch (err) {
      message.error("Failed to fetch companies");
      return [];
    }
  };

  // Fetch products and update state
  const fetchProducts = async (companyId) => {
    if (!companyId) return [];
    try {
      const response = await axios.get(
        `${API_BASE_URL}/products/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts(response.data || []);
      return response.data || [];
    } catch (err) {
      message.error("Failed to fetch products");
      return [];
    }
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        select_type: selectType,
        start_date: values.start_date.format("YYYY-MM-DD"),
        end_date: values.end_date.format("YYYY-MM-DD"),
      };

      // Remove original_price from payload when updating
      if (editingOffer) {
        delete payload.original_price;
        await axios.put(`${API_BASE_URL}/offers/${editingOffer.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Offer updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/offers`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Offer created successfully");
      }
      fetchOffers();
      setModalVisible(false);
      form.resetFields();
    } catch (err) {
      message.error(err.response?.data?.message || err.response?.data?.error);
    }
  };

  // Handle edit offer
  const handleEdit = async (offer) => {
    setEditingOffer(offer);
    setModalVisible(true);
    try {
      let companyList = companies;
      if (companies.length === 0) {
        companyList = await fetchCompanies();
        setCompanies(companyList);
      }
      let companyId = offer.company_id;
      if (!companyId && offer.products && offer.products.length > 0) {
        const product = offer.products[0];
        const company = companyList.find(
          (c) => c.Products && c.Products.some((p) => p.id === product.id)
        );
        if (company) companyId = company.id;
      }
      if (companyId) {
        await fetchProducts(companyId);
      }
      setSelectType(offer.select_type || "description");
      // Set product_ids as an array of IDs
      let productIds = [];
      if (offer.product_ids) {
        productIds = offer.product_ids;
      } else if (offer.products && Array.isArray(offer.products)) {
        productIds = offer.products.map((p) => p.id);
      } else if (offer.Product) {
        productIds = [offer.Product.id];
      } else if (offer.product_id) {
        productIds = [Number(offer.product_id)];
      }
      form.setFieldsValue({
        ...offer,
        company_id: companyId ? Number(companyId) : undefined,
        product_ids: productIds,
        start_date: dayjs(offer.start_date),
        end_date: dayjs(offer.end_date),
      });
    } catch (err) {
      message.error("Failed to load data for editing");
    }
  };

  // Handle delete offer
  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this offer?",
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/offers/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success("Offer deleted successfully");
          fetchOffers();
        } catch (err) {
          message.error("Failed to delete offer");
        }
      },
      onCancel: () => {},
    });
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current: page }));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: newPageSize,
    }));
  };

  // Auto-calculate discounted price on form value changes
  const handleDiscountChange = () => {
    const { discount_type, discount_value, original_price } =
      form.getFieldsValue();

    if (original_price != null && discount_value != null) {
      let discountedPrice = original_price;
      if (discount_type === "percentage") {
        discountedPrice =
          original_price - (original_price * discount_value) / 100;
      } else {
        discountedPrice = original_price - discount_value;
      }
      form.setFieldsValue({ discounted_price: discountedPrice.toFixed(2) });
    }
  };

  // Fetch initial data on mount
  useEffect(() => {
    fetchOffers();
    fetchCompanies();
  }, []);

  return (
    <div className="p-4">
      <Row
        justify="space-between"
        align="middle"
        gutter={[16, 16]}
        style={{ marginBottom: "20px" }}
      >
        <Col xs={24} sm={24} md={12} lg={12}>
          <Title level={2}>Offers</Title>
        </Col>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingOffer(null);
            setModalVisible(true);
            form.resetFields();
            setProducts([]); // Clear products when creating new
          }}
        >
          Create Offer
        </Button>
      </Row>

      {loading ? (
        <Spin tip="Loading offers..." />
      ) : (
        <>
          {offers.length === 0 ? (
            <Empty description="No offers found">
              <Button
                type="primary"
                onClick={() => {
                  setEditingOffer(null);
                  setModalVisible(true);
                  form.resetFields();
                  setProducts([]);
                }}
              >
                Create Your First Offer
              </Button>
            </Empty>
          ) : (
            <>
              <div className="rounded-2 table-responsive">
                <table
                  className="table align-middle mb-0 shadow-sm"
                  style={{ tableLayout: "auto" }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>Title</th>
                      <th>Products</th>
                      <th>Discount</th>
                      <th>Original Price</th>
                      <th>Discounted Price</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers
                      .slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                      )
                      .map((offer) => (
                        <tr key={offer.id}>
                          <td>{offer.title}</td>
                          <td>
                            {offer.products && Array.isArray(offer.products) ? (
                              <div>
                                {offer.products.map((product, index) => (
                                  <Tag key={index} style={{ marginBottom: 4 }}>
                                    {product.name}
                                  </Tag>
                                ))}
                              </div>
                            ) : offer.Product ? (
                              <Tag>{offer.Product.name}</Tag>
                            ) : (
                              <Tag>N/A</Tag>
                            )}
                          </td>
                          <td>
                            {offer.discount_value == null ? (
                              <Tag color={"red"}>N/A</Tag>
                            ) : (
                              <Tag color={offer.is_active ? "green" : "red"}>
                                {offer.discount_type === "percentage"
                                  ? `${offer.discount_value}%`
                                  : `₹${offer.discount_value}`}
                              </Tag>
                            )}
                          </td>
                          <td>₹{offer.original_price}</td>
                          <td>
                            {offer.discounted_price == null
                              ? "N/A"
                              : `₹${offer.discounted_price}`}
                          </td>
                          <td>
                            <Tag color={offer.is_active ? "green" : "red"}>
                              {offer.is_active ? "Active" : "Inactive"}
                            </Tag>
                          </td>
                          <td
                            style={{
                              paddingRight: "8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Button
                              type="text"
                              icon={<EditOutlined style={{ color: "green" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(offer);
                              }}
                            />
                            <Button
                              type="text"
                              icon={<DeleteOutlined style={{ color: "red" }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(offer.id);
                              }}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end">
                <CustomPagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </>
          )}
        </>
      )}

      <Modal
        title={editingOffer ? "Edit Offer" : "Create Offer"}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={handleDiscountChange}
        >
          <Form.Item
            name="company_id"
            label="Company"
            rules={[{ required: true, message: "Please select a company" }]}
          >
            <Select
              placeholder="Select Company"
              onChange={(companyId) => {
                form.setFieldsValue({ product_ids: [] });
                fetchProducts(companyId);
              }}
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.company_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="product_ids"
            label="Products"
            rules={[
              { required: true, message: "Please select at least one product" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select Products"
              disabled={!form.getFieldValue("company_id")}
              showSearch={true}
              filterOption={(input, option) => {
                if (!option?.children) return false;
                if (typeof option.children === "string") {
                  return option.children
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }
                // If children is an array or React element, convert to string and search
                const childrenText = Array.isArray(option.children)
                  ? option.children.join(" ").toLowerCase()
                  : option.children.toString().toLowerCase();
                return childrenText.includes(input.toLowerCase());
              }}
              onChange={(selectedProductIds) => {
                const allProductIds = products.map((p) => p.id);
                const selectAllValue = "all_products";

                if (selectedProductIds.includes(selectAllValue)) {
                  // If "Select All" is selected, select all products
                  form.setFieldsValue({ product_ids: allProductIds });
                  if (allProductIds.length > 0) {
                    const firstProduct = products.find(
                      (p) => p.id === allProductIds[0]
                    );
                    if (firstProduct) {
                      form.setFieldsValue({
                        original_price: firstProduct.price,
                        discounted_price: firstProduct.price,
                      });
                    }
                  }
                } else {
                  // Normal selection
                  form.setFieldsValue({ product_ids: selectedProductIds });
                  if (selectedProductIds.length > 0) {
                    const firstProduct = products.find(
                      (p) => p.id === selectedProductIds[0]
                    );
                    if (firstProduct) {
                      form.setFieldsValue({
                        original_price: firstProduct.price,
                        discounted_price: firstProduct.price,
                      });
                    }
                  } else {
                    form.setFieldsValue({
                      original_price: undefined,
                      discounted_price: undefined,
                    });
                  }
                }
              }}
              value={form.getFieldValue("product_ids")}
            >
              <Option key="all_products" value="all_products">
                Select All
              </Option>
              {products.map((product) => (
                <Option key={product.id} value={product.id}>
                  {product.name} - ₹{product.price}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter a title" }]}
          >
            <Input placeholder="Offer title" />
          </Form.Item>

          {/* Remove the initial description and discount_type fields before Select Type */}
          {/* New radio button group for Select Type */}
          <Form.Item label="Select Type" required>
            <Radio.Group
              onChange={(e) => {
                setSelectType(e.target.value);
                // Reset fields when switching
                if (e.target.value === "description") {
                  form.setFieldsValue({
                    discount_type: undefined,
                    discount_value: undefined,
                    discounted_price: undefined,
                  });
                } else {
                  form.setFieldsValue({ description: undefined });
                }
              }}
              value={selectType}
            >
              <Radio value="description">Description</Radio>
              <Radio value="discount_type">Discount Type</Radio>
            </Radio.Group>
          </Form.Item>

          {/* Conditionally render Description or Discount Type fields */}
          {selectType === "description" ? (
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <TextArea rows={3} placeholder="Offer description" />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="discount_type"
                label="Discount Type"
                rules={[
                  { required: true, message: "Please select discount type" },
                ]}
              >
                <Select>
                  <Option value="percentage">Percentage</Option>
                  <Option value="fixed_amount">Fixed Amount</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="discount_value"
                label="Discount Value"
                rules={[
                  { required: true, message: "Please enter discount value" },
                ]}
              >
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder={
                    form.getFieldValue("discount_type") === "percentage"
                      ? "0-100"
                      : "Amount"
                  }
                />
              </Form.Item>

              <Form.Item
                name="discounted_price"
                label="Discounted Price"
                rules={[
                  { required: true, message: "Please enter discounted price" },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="original_price"
            label="Original Price"
            rules={[
              {
                required: !editingOffer,
                message: "Please enter original price",
              },
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              disabled={!!editingOffer}
            />
          </Form.Item>

          <Form.Item
            name="start_date"
            label="Start Date"
            rules={[{ required: true, message: "Please select start date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="end_date"
            label="End Date"
            rules={[{ required: true, message: "Please select end date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="is_active" valuePropName="checked">
            <Checkbox>Is Active</Checkbox>
          </Form.Item>

          <Form.Item
            style={{
              position: "sticky",
              bottom: 0,
              background: "white",
              zIndex: 1000,
            }}
          >
            <Button type="primary" htmlType="submit" block>
              {editingOffer ? "Update Offer" : "Create Offer"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OfferManagement;
